import { useContext, useEffect, useMemo, useState } from "react";

import CloseIcon from "assets/icons/close.svg?react";
import PDFViewer from "components/PDFViewer";

import KeysContext from "contexts/KeysContext";
import usePDF from "hooks/usePDF";
import dispatchChartService from "api/dispatchChartService";
import { IAttachment } from "dto/IAttachment";
import { IKey } from "dto/IKey";
import { ItemKind } from "dto/IKindItems";

import styles from "./outgoing.module.scss";

interface Props {
  parcelId: string;
  title?: string | null;
  onClose: () => void;
}

/**
 * Podgląd treści przesyłki (wygenerowane pismo / PDF). Pobiera plik + klucze obiektu
 * z endpointu podglądu (owner-scope), ładuje klucze do KeysContext i deszyfruje
 * klientowo (model E2E). Wzorzec jak w AcceptanceFilePreview.
 */
const ParcelPreviewModal = ({ parcelId, title, onClose }: Props) => {
  const { data } = dispatchChartService.useGetParcelPreview(parcelId);
  const preview = data?.data;

  const { keys, setKeys } = useContext(KeysContext);
  const { decryptPackage } = usePDF();

  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Załaduj klucze obiektu + użytkownika/spraw do KeysContext (inaczej deszyfrowanie = nokey).
  useEffect(() => {
    if (!preview) return;
    const incoming: IKey[] = [
      ...(preview.ObjectKeys ?? []),
      ...(preview.Keys ?? []),
    ];
    const missing = incoming.filter((k) => !keys.some((x) => x.Id === k.Id));
    if (missing.length > 0) setKeys([...keys, ...missing]);
  }, [preview, keys, setKeys]);

  const attachment: Partial<IAttachment> | null = useMemo(() => {
    if (!preview?.Uri || !preview.EncryptionKeyId) return null;
    return {
      Id: preview.Uri,
      Uri: preview.Uri,
      Kind: preview.Kind as ItemKind,
      EncryptionKeyId: preview.EncryptionKeyId,
      FileName: preview.FileName ?? title ?? "pismo.pdf",
      ContentType: "application/pdf",
      Format: "pdf",
      Label: preview.FileName ?? "",
      Size: 0,
    };
  }, [preview, title]);

  const keyReady =
    !!attachment && keys.some((k) => k.Id === attachment.EncryptionKeyId);

  useEffect(() => {
    let cancelled = false;
    setBlob(null);
    setError(null);
    if (!attachment || !keyReady) return;
    setLoading(true);
    (async () => {
      try {
        const b = await decryptPackage(attachment, attachment.Kind, false);
        if (!cancelled) setBlob(b);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment?.Uri, attachment?.EncryptionKeyId, keyReady]);

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.previewModal}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.previewHead}>
          <span>{preview?.FileName ?? title ?? "Podgląd pisma"}</span>
          <CloseIcon className={styles.closeIcon} onClick={onClose} />
        </div>
        <div className={styles.previewBody}>
          {error ? (
            <div className={styles.error}>
              Nie udało się odszyfrować treści pisma. Klucz musi być dostępny dla
              bieżącego użytkownika (autora). Błąd: {error}
            </div>
          ) : blob ? (
            <PDFViewer file={blob} title={preview?.FileName ?? undefined} />
          ) : preview && !attachment ? (
            <div className={styles.empty}>
              Ta przesyłka nie ma jeszcze wygenerowanego pisma do podglądu.
            </div>
          ) : (
            <div className={styles.empty}>
              {loading ? "Deszyfrowanie…" : "Ładowanie podglądu…"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParcelPreviewModal;
